import {Container, Hidden} from '@material-ui/core';
import {APIResponseStatus, NormalizedRoutes, RoleId, UserInfo} from 'common/types';
import ChipsSquare from 'components/ChipsSquare';
import Scrollable from 'components/Scrollable';
import Typo from 'components/Typo';
import {useCurrentUser} from 'hooks/user';
import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';
import {ChipOption} from 'screens/DealsScreen/config';
import {MlsAPI, MlsCounters} from 'services/MlsAPI';

export interface MlsSection {
  url: string;
  label: string;
  counterKey: keyof MlsCounters;
  counterPlus?: number;
}

const isMlsRealtor = (user: UserInfo) => user.verified && user.roleId === RoleId.Realtor;

// Sections grouped under "МЛС" menu item, in display order
export const getMlsSections = (user: UserInfo | null): MlsSection[] => {
  if (user === null) {
    return [];
  }

  const sections: MlsSection[] = [];

  if (isMlsRealtor(user)) {
    sections.push({
      url: NormalizedRoutes.Objects,
      label: 'user_profile_menu.objects',
      counterKey: 'objects',
    });
    sections.push({
      url: NormalizedRoutes.Rieltors,
      label: 'user_profile_menu.rieltors',
      counterKey: 'rieltors',
      counterPlus: user.partnersRequests,
    });
  }

  if ([RoleId.Realtor, RoleId.AgencyAdmin].includes(user.roleId)) {
    sections.push({
      url: NormalizedRoutes.DealsManagement,
      label: 'user_profile_menu.my_deals',
      counterKey: 'deals',
    });
  }

  return sections;
};

// "МЛС" is shown only to users with several sections (verified rieltors).
// Others (agency admin, unverified rieltor) have Deals only and keep plain "Угоди" menu item.
export const hasMlsMenu = (user: UserInfo | null): boolean => getMlsSections(user).length > 1;

export const MLS_URLS: string[] = [
  NormalizedRoutes.Rieltors,
  NormalizedRoutes.Objects,
  NormalizedRoutes.DealsManagement,
];

export const isMlsUrl = (pathname: string) => MLS_URLS.some(url => pathname === url || pathname.startsWith(`${url}/`));

// Second level navigation of "МЛС": mobile page title + sections chips.
// Rendered by MainLayout, stays mounted while switching between sections.
const MlsSections = () => {
  const user = useCurrentUser();
  const history = useHistory();
  const {pathname} = useLocation();
  const {formatMessage} = useIntl();
  const [counters, setCounters] = useState<null | MlsCounters>(null);

  const sections = getMlsSections(user);
  const isMls = hasMlsMenu(user);

  useEffect(() => {
    if (!isMls) {
      return;
    }

    (new MlsAPI()).counters()
      .then(response => {
        if (response.status === APIResponseStatus.Ok) {
          setCounters(response.data);
        }
      })
      .catch(error => {
        console.debug(error);
      });
  }, [isMls]);

  const activeSection = sections.find(section => pathname.startsWith(section.url));

  const options: ChipOption[] = sections.map(section => ({
    value: section.url,
    label: formatMessage({id: section.label}),
    counter: counters ? counters[section.counterKey] : undefined,
    counterPlus: section.counterPlus,
  }));

  if (!isMls) {
    return null;
  }

  return (
    <Container maxWidth="xl" style={{padding: '16px 16px 0'}}>
      <Hidden mdUp>
        <Typo variant={'title1'} style={{marginBottom: '16px'}}>
          {formatMessage({id: 'user_profile_menu.mls'})}
        </Typo>
      </Hidden>

      <Scrollable>
        <ChipsSquare
          value={activeSection ? activeSection.url : null}
          options={options}
          onChange={chip => history.push(chip.value)}
        />
      </Scrollable>
    </Container>
  );
};

export default MlsSections;
